import * as React from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowRight, WhatsApp } from "Components/ui/icons";

import { startLogin } from "api/auth";
import { getApiErrorCode, getApiErrorMessage, getApiErrorStatus } from "api/apiError";
import { Alert, Button, FormField, Input, PasswordInput } from "Components/ui";
import { StepHeader } from "./AuthLayout";
import { useAuthFlow } from "./data";
import { AUTH_PATHS } from "./types";

const schema = z.object({
  userName: z.string().trim().min(1, "Enter your username"),
  password: z.string().min(1, "Enter your password"),
});

type SignInValues = z.infer<typeof schema>;

interface SignInError {
  title: string;
  message: string;
}

// Each failure says what happened and what to do next. A wrong username and a wrong password get
// the same answer, so the form never confirms which accounts exist.
const signInError = (err: unknown): SignInError => {
  switch (getApiErrorCode(err)) {
    case "AUTH_OTP_PHONE_MISSING":
      return {
        title: "Verification isn't set up",
        message:
          "No WhatsApp number is linked to this account, so we can't send a verification code. Contact your administrator to add one.",
      };
    case "AUTH_OTP_SEND_FAILED":
      return {
        title: "We couldn't send your verification code",
        message: "Please try again in a few minutes.",
      };
    case "AUTH_FORBIDDEN":
      return {
        title: "Account suspended",
        message: "Contact your administrator to restore access.",
      };
    case "AUTH_RATE_LIMITED":
      return {
        title: "Too many sign-in attempts",
        message: "For your security, sign-in is paused for a few minutes. Please try again later.",
      };
    default:
      break;
  }
  const status = getApiErrorStatus(err);
  if (status === 401) {
    return {
      title: "Incorrect username or password",
      message: "Check your details and try again. Passwords are case-sensitive.",
    };
  }
  if (status === null) {
    return {
      title: "Can't reach the server",
      message: "Check your internet connection and try again.",
    };
  }
  if (status >= 500) {
    return {
      title: "Something went wrong",
      message:
        "Please try again in a moment. If the problem continues, contact your administrator.",
    };
  }
  return { title: "Sign-in failed", message: getApiErrorMessage(err, "Please try again.") };
};

const SignInStep = () => {
  const flow = useAuthFlow();
  const [serverError, setServerError] = React.useState<SignInError | null>(null);
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<SignInValues>({
    resolver: zodResolver(schema),
    defaultValues: { userName: flow.userName, password: "" },
  });

  const onSubmit = async ({ userName, password }: SignInValues) => {
    setServerError(null);
    flow.beginLocationLookup();
    try {
      const challenge = await startLogin({ userName: userName.trim(), password });
      flow.startChallenge(userName.trim(), {
        userId: challenge.userId,
        phoneHint: challenge.phoneHint,
        expiresInSeconds: challenge.expiresInSeconds,
        resendAfterSeconds: challenge.resendAfterSeconds,
      });
    } catch (err) {
      setServerError(signInError(err));
    }
  };

  return (
    <>
      <StepHeader
        step={{ current: 1, total: 2, label: "Sign in" }}
        title="Sign in to Super Admin"
        description="Use your SAP username and password."
      />

      <div className="space-y-5">
        {flow.notice && (
          <Alert tone={flow.notice.tone} title={flow.notice.title} onDismiss={flow.clearNotice}>
            {flow.notice.message}
          </Alert>
        )}
        {serverError && (
          <Alert tone="danger" title={serverError.title}>
            {serverError.message}
          </Alert>
        )}

        <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <FormField label="Username" error={errors.userName?.message}>
            <Input
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus={!flow.userName}
              {...register("userName")}
            />
          </FormField>

          <FormField
            label="Password"
            error={errors.password?.message}
            labelAction={
              <Link
                to={AUTH_PATHS.forgot}
                onClick={() => flow.setUserName(getValues("userName").trim())}
                className="rounded-sm text-sm font-medium text-primary-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Forgot password?
              </Link>
            }
          >
            <PasswordInput
              autoComplete="current-password"
              autoFocus={Boolean(flow.userName)}
              {...register("password")}
            />
          </FormField>

          <Button
            type="submit"
            size="lg"
            fullWidth
            loading={isSubmitting}
            rightIcon={<ArrowRight />}
          >
            Continue
          </Button>
        </form>

        <p className="flex items-start gap-2.5 rounded-card border border-border bg-surface-muted/60 px-3.5 py-3 text-xs leading-5 text-muted-foreground">
          <WhatsApp aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal" />
          Next, you'll confirm it's you with a 6-digit code sent to your WhatsApp.
        </p>
      </div>
    </>
  );
};

export default SignInStep;
